// EL INVENTARIO EN DOS HOJAS: lo que hay, y de dónde sale.
//
// Esto es solo la lógica. No sabe nada de Excel ni de Supabase: recibe lo que
// devuelven las dos lecturas y devuelve filas. `InventarioPage` las pinta en el
// `.xlsx` y la prueba las afirma sin montar la página.
//
// ── DE DÓNDE SALE «VENDIDAS», Y POR QUÉ DEL LIBRO Y NO DE LOS PEDIDOS ──────
// `stock_movements` es un libro de verdad: `trg_stock_movements_no_mutate`
// bloquea UPDATE y DELETE, y `variant_id` es NOT NULL. Medido contra
// producción: en las 12 variantes, `stock` = Σ `delta`, descuadre CERO. Así que
// el número se lee del libro.
//
// Reconstruirlo de `orders` sería peor y encima incompleto: no hay tabla de
// líneas —son `orders.items`, jsonb— y 9 pedidos tienen movimiento de venta sin
// una sola línea de stock físico, porque la prenda viajaba dentro de un pack y
// la línea del pack no lleva `variant_id`. **18 de las 32 unidades vendidas
// salieron así.** El libro las tiene todas; los pedidos, no.
//
// ── LA HOJA CUADRA, Y ESO ES EL REQUISITO ─────────────────────────────────
// Las columnas de la hoja 1 son las razones del libro, ni una más:
//
//     inicial + ajustes − vendidas − regaladas + devueltas = stock hoy
//
// Es una identidad que se puede comprobar en Excel, fila a fila. Si en vez de
// eso la hoja llevara solo «vendidas», habría que restar a mano para saber si
// el stock es el que dice. `cuadra()` afirma esa identidad y la prueba la
// ejecuta sobre cada fila.

import { etiquetaDeLaFormaDePago } from './formasDePago';
import { etiquetaVariante } from './variantes';

// ── Lo que entra ──────────────────────────────────────────────────────────

export interface VarianteDeInventario {
  id: string;
  attributes: Record<string, unknown> | null;
  stock: number;
  position: number;
  active: boolean;
  sku: string | null;
}

export interface ProductoDeInventario {
  id: string;
  name: string;
  active: boolean;
  variantes: VarianteDeInventario[];
}

/**
 * Una línea de `orders.items`.
 *
 * ⚠️ TODO es opcional, y no por prudencia: es una columna `jsonb` sellada en el
 * checkout, sin esquema que la obligue. Medido sobre los 49 pedidos de
 * producción: la clave `variant_id` aparece en 32 líneas y solo tiene VALOR en
 * 23 —en las 9 restantes está a `null`—. Preguntar si la clave existe no sirve;
 * hay que mirar el valor.
 */
export interface LineaDePedido {
  kind?: string | null;
  name?: string | null;
  variant_id?: string | null;
  quantity?: number | null;
  final_cents?: number | null;
  fulfillment_type?: string | null;
  /**
   * El interior de un pack. Es un OBJETO `{ v, items }`, no un array — medido:
   * `jsonb_typeof` da `object` en las 14 líneas de pack que hay. Sus `items`
   * sí llevan `variant_id` y `size`, así que la TALLA se recupera; el PRECIO
   * no, porque el pack tiene un solo `final_cents` para todo.
   */
  components?: { v?: number | null; items?: LineaDePedido[] | null } | null;
}

export interface MovimientoDeStock {
  variant_id: string;
  delta: number;
  reason: string;
  note: string | null;
  created_at: string;
  variante: {
    attributes: Record<string, unknown> | null;
    sku: string | null;
    producto: { name: string } | null;
  } | null;
  pedido: {
    order_number: number | null;
    items: LineaDePedido[] | null;
    payment_method: string | null;
    comprador: { full_name: string | null } | null;
  } | null;
}

// ── El precio de una unidad ───────────────────────────────────────────────

/**
 * Por qué una unidad tiene precio, o por qué no lo tiene.
 *
 * ⚠️ NO es `number | null`. Un `null` obligaría a la hoja a inventarse una
 * explicación, y hay TRES motivos distintos para no tener precio: que la prenda
 * fuera dentro de un pack (el caso de 18 de las 32 vendidas), que el movimiento
 * no venga de ningún pedido (un ajuste a mano, el stock inicial), o que haya
 * pedido y su `items` no nombre esa variante —que no debería pasar y por eso
 * tiene su propio nombre—.
 *
 * Decirle «dentro de un pack» a lo que en realidad es «no lo sé» sería
 * exactamente el fallo que evitamos poniendo `0,00 €`: una celda que afirma algo
 * que no se ha comprobado.
 */
export type PrecioDeLaUnidad =
  | { clase: 'suelto'; cents: number }
  | { clase: 'en_pack' }
  | { clase: 'sin_pedido' }
  | { clase: 'no_encontrado' };

/** El texto que va a la celda. Un solo sitio decide cómo se lee cada caso. */
export function textoDelPrecio(p: PrecioDeLaUnidad): string {
  switch (p.clase) {
    case 'en_pack':
      return 'dentro de un pack';
    case 'sin_pedido':
      return 'sin pedido';
    case 'no_encontrado':
      return 'sin precio en el pedido';
    case 'suelto':
      return '';
  }
}

function numeroFinito(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function nombraLaVariante(li: LineaDePedido, variantId: string): boolean {
  return typeof li.variant_id === 'string' && li.variant_id === variantId;
}

/**
 * El precio por unidad de `variantId` dentro de un pedido.
 *
 * Primero las líneas de arriba: si una nombra la variante y trae importe, el
 * precio es ese importe partido por sus unidades. Y solo si no está arriba se
 * baja al interior de los packs — porque ahí la respuesta no es un número, es
 * «va dentro de un pack», y encontrarla arriba es siempre mejor.
 */
export function precioDeLaUnidad(
  items: LineaDePedido[] | null | undefined,
  variantId: string
): PrecioDeLaUnidad {
  const lineas = Array.isArray(items) ? items : [];

  for (const li of lineas) {
    if (!nombraLaVariante(li, variantId)) continue;
    const cents = numeroFinito(li.final_cents);
    if (cents === null) continue;
    const q = numeroFinito(li.quantity);
    const unidades = q !== null && q >= 1 ? q : 1;
    return { clase: 'suelto', cents: Math.round(cents / unidades) };
  }

  for (const li of lineas) {
    const dentro = li.components?.items;
    if (!Array.isArray(dentro)) continue;
    if (dentro.some((c) => nombraLaVariante(c, variantId))) return { clase: 'en_pack' };
  }

  return { clase: 'no_encontrado' };
}

// ── Hoja 1 · Inventario ───────────────────────────────────────────────────

export interface FilaDeInventario {
  producto: string;
  productoActivo: boolean;
  /**
   * ⚠️ La MISMA etiqueta que la pantalla, por `etiquetaVariante`. La columna se
   * titula «Variante» y no «Talla» justo por eso: hoy el único eje es la talla
   * —12 variantes, todas con `talla`— pero `variantes.ts` ya contempla el color,
   * y una hoja que leyera `attributes.talla` a mano se quedaría muda el día que
   * entre el segundo eje, mientras la pantalla seguiría diciéndolo.
   */
  variante: string;
  sku: string | null;
  inicial: number;
  ajustes: number;
  vendidas: number;
  regaladas: number;
  devueltas: number;
  stock: number;
  activa: boolean;
  /** La fila de totales del producto: no es una variante. */
  esTotal: boolean;
}

/** `inicial + ajustes − vendidas − regaladas + devueltas === stock` */
export function cuadra(f: FilaDeInventario): boolean {
  return f.inicial + f.ajustes - f.vendidas - f.regaladas + f.devueltas === f.stock;
}

function sumaDeltas(movs: MovimientoDeStock[], razones: string[]): number {
  return movs.filter((m) => razones.includes(m.reason)).reduce((s, m) => s + m.delta, 0);
}

/**
 * Lo mismo que `sumaDeltas` pero con el signo girado, para las razones que
 * restan (`sale`, `gift`): la hoja las cuenta en positivo.
 *
 * ⚠️ El `=== 0` NO es redundante. `-0` es un número de verdad en JavaScript, y
 * una variante sin ninguna venta daba `-sumaDeltas([]) === -0`: en la celda de
 * Excel eso se lee como un error de cálculo, no como «ninguna». Lo cazó la
 * prueba de la variante sin movimientos.
 *
 * Y se gira el signo en vez de usar `Math.abs` a propósito: si algún día un
 * movimiento de `sale` llegara con `delta` positivo —un dato mal escrito—,
 * `abs` lo taparía y aquí sale negativo y rompe `cuadra()`, que es lo que
 * queremos que pase.
 */
function sumaRestada(movs: MovimientoDeStock[], razones: string[]): number {
  const suma = -sumaDeltas(movs, razones);
  return suma === 0 ? 0 : suma;
}

export function filasDeInventario(
  productos: ProductoDeInventario[],
  movimientos: MovimientoDeStock[]
): FilaDeInventario[] {
  const porVariante = new Map<string, MovimientoDeStock[]>();
  for (const m of movimientos) {
    const lista = porVariante.get(m.variant_id);
    if (lista) lista.push(m);
    else porVariante.set(m.variant_id, [m]);
  }

  const filas: FilaDeInventario[] = [];
  for (const p of productos) {
    // El orden lo da `position`, como en la pantalla.
    const variantes = [...p.variantes].sort((a, b) => a.position - b.position);
    const deEsteProducto: FilaDeInventario[] = variantes.map((v) => {
      const movs = porVariante.get(v.id) ?? [];
      return {
        producto: p.name,
        productoActivo: p.active,
        variante: etiquetaVariante(v.attributes),
        sku: v.sku,
        inicial: sumaDeltas(movs, ['initial']),
        // Firmado a propósito: una corrección a la baja es negativa y la hoja
        // tiene que poder decirlo, o la identidad de arriba no se cumple.
        ajustes: sumaDeltas(movs, ['manual', 'correction']),
        vendidas: sumaRestada(movs, ['sale']),
        regaladas: sumaRestada(movs, ['gift']),
        devueltas: sumaDeltas(movs, ['devolucion']),
        stock: v.stock,
        activa: v.active,
        esTotal: false,
      };
    });
    filas.push(...deEsteProducto);
    if (deEsteProducto.length > 0) {
      filas.push({
        producto: p.name,
        productoActivo: p.active,
        variante: 'TOTAL',
        sku: null,
        inicial: deEsteProducto.reduce((s, f) => s + f.inicial, 0),
        ajustes: deEsteProducto.reduce((s, f) => s + f.ajustes, 0),
        vendidas: deEsteProducto.reduce((s, f) => s + f.vendidas, 0),
        regaladas: deEsteProducto.reduce((s, f) => s + f.regaladas, 0),
        devueltas: deEsteProducto.reduce((s, f) => s + f.devueltas, 0),
        stock: deEsteProducto.reduce((s, f) => s + f.stock, 0),
        activa: true,
        esTotal: true,
      });
    }
  }
  return filas;
}

// ── Hoja 2 · Ventas ───────────────────────────────────────────────────────

/**
 * Los tres motivos que mueven género por una venta, un regalo o una devolución.
 * `initial`, `manual` y `correction` NO entran: son almacén, y van resumidos en
 * la hoja 1. Si entraran, la lista de ventas dejaría de ser una lista de ventas.
 */
export const MOTIVOS_DE_LA_HOJA = ['sale', 'gift', 'devolucion'] as const;

const MOTIVO_EN_LA_HOJA: Record<string, string> = {
  sale: 'Venta',
  gift: 'Regalo',
  devolucion: 'Devolución',
};

export interface FilaDeVenta {
  fecha: Date;
  pedido: number | null;
  articulo: string;
  variante: string;
  /**
   * ⚠️ SIEMPRE POSITIVO, y el motivo dice el sentido. En el libro una
   * devolución es `delta = +1` y una venta `delta = −1`: pasar ese signo a la
   * hoja invita a sumar la columna y obtener algo que no significa nada.
   */
  unidades: number;
  motivo: string;
  alumno: string;
  precio: PrecioDeLaUnidad;
  formaDePago: string;
  nota: string | null;
}

export function filasDeVentas(movimientos: MovimientoDeStock[]): FilaDeVenta[] {
  return movimientos
    .filter((m) => (MOTIVOS_DE_LA_HOJA as readonly string[]).includes(m.reason))
    .slice()
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .map((m) => ({
      fecha: new Date(m.created_at),
      pedido: m.pedido?.order_number ?? null,
      articulo: m.variante?.producto?.name ?? '(producto borrado)',
      variante: etiquetaVariante(m.variante?.attributes),
      unidades: Math.abs(m.delta),
      motivo: MOTIVO_EN_LA_HOJA[m.reason] ?? m.reason,
      // Un movimiento de regalo o de venta siempre tiene pedido —medido: 38 de
      // 38—, pero el comprador puede haber borrado su cuenta: la FK de
      // `orders.user_id` es ON DELETE SET NULL y el perfil se anonimiza.
      alumno: m.pedido?.comprador?.full_name ?? '(sin cuenta)',
      precio: m.pedido ? precioDeLaUnidad(m.pedido.items, m.variant_id) : { clase: 'sin_pedido' },
      formaDePago: etiquetaDeLaFormaDePago(m.pedido?.payment_method),
      nota: m.note,
    }));
}
